import React from 'react';
import Select, { StylesConfig } from 'react-select';
import { AlertCircle } from 'lucide-react';

export interface SelectOption {
  value: string;
  label: string;
  [key: string]: any;
}

export interface SelectFieldProps {
  id?: string;
  name?: string;
  label?: string | React.ReactNode;
  required?: boolean;
  error?: string;
  helperText?: string;
  placeholder?: string;
  value?: string;
  options: Array<SelectOption | { _id: string; name: string }>;
  onChange: (value: string) => void;
  onBlur?: () => void;
  isLoading?: boolean;
  isDisabled?: boolean;
  isClearable?: boolean;
  isSearchable?: boolean;
  containerClassName?: string;
}

export const SelectField: React.FC<SelectFieldProps> = ({
  id,
  name,
  label,
  required,
  error,
  helperText,
  placeholder = 'Select...',
  value,
  options = [],
  onChange,
  onBlur,
  isLoading = false,
  isDisabled = false,
  isClearable = true,
  isSearchable = true,
  containerClassName = '',
}) => {
  const selectId = id || (name ? `select_${name}` : undefined);

  // Normalize options to { value, label } format
  const normalizedOptions: SelectOption[] = React.useMemo(() => {
    if (!Array.isArray(options)) return [];
    return options.map((opt: any) => {
      if (opt && typeof opt === 'object') {
        if ('value' in opt && 'label' in opt) {
          return { value: String(opt.value), label: String(opt.label) };
        }
        if ('_id' in opt && 'name' in opt) {
          return { value: String(opt._id), label: String(opt.name) };
        }
      }
      return { value: String(opt), label: String(opt) };
    });
  }, [options]);

  const selectedOption = React.useMemo(() => {
    if (!value) return null;
    return normalizedOptions.find((opt) => opt.value === value) || null;
  }, [value, normalizedOptions]);

  // Compact Modern Styles matching the new rounded-xl aesthetic
  const customStyles: StylesConfig<SelectOption, false> = {
    control: (provided, state) => ({
      ...provided,
      minHeight: '38px',
      borderRadius: '12px',
      borderColor: error
        ? '#fca5a5'
        : state.isFocused
        ? '#1AA7DE'
        : '#e2e8f0',
      backgroundColor: state.isDisabled ? '#f1f5f9' : '#f8fafc',
      boxShadow: state.isFocused
        ? error
          ? '0 0 0 3px rgba(244, 63, 94, 0.15)'
          : '0 0 0 3px rgba(26, 167, 222, 0.15)'
        : 'none',
      cursor: state.isDisabled ? 'not-allowed' : 'pointer',
      transition: 'all 0.15s ease-in-out',
      '&:hover': {
        borderColor: error ? '#f87171' : state.isFocused ? '#1AA7DE' : '#cbd5e1',
        backgroundColor: state.isDisabled ? '#f1f5f9' : '#ffffff',
      },
    }),
    valueContainer: (provided) => ({
      ...provided,
      padding: '1px 12px',
    }),
    placeholder: (provided) => ({
      ...provided,
      color: '#94a3b8',
      fontSize: '13px',
      fontWeight: '500',
    }),
    singleValue: (provided, state) => ({
      ...provided,
      color: state.isDisabled ? '#94a3b8' : '#1e293b',
      fontSize: '13px',
      fontWeight: '600',
    }),
    input: (provided) => ({
      ...provided,
      color: '#1e293b',
      fontSize: '13px',
      margin: '0',
      padding: '0',
    }),
    menu: (provided) => ({
      ...provided,
      borderRadius: '12px',
      border: '1px solid #f1f5f9',
      boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 8px 10px -6px rgba(0, 0, 0, 0.1)',
      zIndex: 9999,
      overflow: 'hidden',
      padding: '4px',
      backgroundColor: '#ffffff',
    }),
    menuList: (provided) => ({
      ...provided,
      padding: '2px',
      maxHeight: '220px',
    }),
    option: (provided, state) => ({
      ...provided,
      fontSize: '13px',
      fontWeight: state.isSelected ? '700' : '500',
      padding: '7px 12px',
      borderRadius: '8px',
      cursor: 'pointer',
      backgroundColor: state.isSelected
        ? '#1AA7DE'
        : state.isFocused
        ? '#f0f9ff'
        : 'transparent',
      color: state.isSelected ? '#ffffff' : state.isFocused ? '#0284c7' : '#334155',
      '&:active': {
        backgroundColor: '#1AA7DE',
        color: '#ffffff',
      },
    }),
    indicatorSeparator: () => ({
      display: 'none',
    }),
    dropdownIndicator: (provided, state) => ({
      ...provided,
      color: state.isDisabled ? '#cbd5e1' : '#94a3b8',
      padding: '4px 8px',
      '&:hover': {
        color: '#334155',
      },
    }),
    clearIndicator: (provided) => ({
      ...provided,
      color: '#cbd5e1',
      padding: '4px',
      '&:hover': {
        color: '#64748b',
      },
    }),
  };

  return (
    <div className={`mb-3 relative ${containerClassName}`}>
      {label && (
        <label htmlFor={selectId} className="block text-xs sm:text-sm font-bold text-slate-700 tracking-wide mb-1">
          {label}
          {required && <span className="text-rose-500 ms-1">*</span>}
        </label>
      )}

      <div>
        <Select<SelectOption, false>
          inputId={selectId}
          name={name}
          styles={customStyles}
          options={normalizedOptions}
          value={selectedOption}
          onChange={(option) => {
            onChange(option ? (option as SelectOption).value : '');
          }}
          onBlur={onBlur}
          placeholder={placeholder}
          isLoading={isLoading}
          isDisabled={isDisabled}
          isClearable={isClearable}
          isSearchable={isSearchable}
          aria-invalid={!!error}
        />
      </div>

      {error ? (
        <div className="flex items-center gap-1.5 mt-1 text-xs font-semibold text-rose-600" role="alert">
          <AlertCircle className="w-3.5 h-3.5 shrink-0" />
          <span>{error}</span>
        </div>
      ) : helperText ? (
        <p className="text-xs text-slate-400 font-normal mt-1 mb-0">{helperText}</p>
      ) : null}
    </div>
  );
};

export default SelectField;
